import {
  toSeoProps,
  type UmbrellaSeoRoutes,
} from '@saas-pages/shared/public/seo'
import type { SeoProps } from '@saas-pages/shared/public/seo'
import { buildUtilities, UTILITY_DEFINITIONS, type UtilityKey } from '@/shared/utilities/utilities'
import { DEFAULT_LOCALE, type Locale } from '@/shared/i18n/locale'
import { enTranslations } from '@/shared/i18n/en'
import { ruTranslations, type Translations } from '@/shared/i18n/ru'
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildHowToSchema,
  buildSoftwareApplicationSchema,
} from '@/shared/seo/schema'
import branding from '../../branding.config'

export type StaticRouteKey = 'index' | 'pdfKonverter' | 'blog'
export type RouteKey = StaticRouteKey | UtilityKey

const russianUtilityByKey = new Map(buildUtilities(ruTranslations).map((utility) => [utility.key, utility]))

function buildPdfKonverterSchemas(umbrella: Translations['umbrella']): object[] {
  return [
    buildBreadcrumbSchema([
      { name: branding.domain, url: `https://${branding.domain}/` },
      { name: umbrella.h1, url: `https://${branding.domain}/pdf-konverter` },
    ]),
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: umbrella.schemaName,
      url: `https://${branding.domain}/pdf-konverter`,
      applicationCategory: 'UtilitiesApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: 0,
        priceCurrency: 'RUB',
      },
    },
    buildFaqSchema(umbrella.faq),
  ]
}

const utilitySeoRoutes = Object.fromEntries(
  UTILITY_DEFINITIONS.map((utility) => {
    const utilityCopy = russianUtilityByKey.get(utility.key)
    if (!utilityCopy) throw new Error(`Missing Russian source metadata for utility ${utility.key}`)
    const schemas: object[] = [
      buildBreadcrumbSchema([
        { name: branding.domain, url: `https://${branding.domain}/` },
        { name: utilityCopy.title, url: `https://${branding.domain}${utility.path}` },
      ]),
      buildSoftwareApplicationSchema({
        name: utilityCopy.title,
        description: utilityCopy.shortDescription,
        category: 'UtilitiesApplication',
        url: `https://${branding.domain}${utility.path}`,
      }),
    ]
    if (utilityCopy.howTo.length > 0) schemas.push(buildHowToSchema(utilityCopy.title, utilityCopy.howTo))
    if (utilityCopy.faq.length > 0) schemas.push(buildFaqSchema(utilityCopy.faq))
    return [utility.key, {
    ...utility.seo,
    jsonLd: {
      mode: 'required' as const,
      schemas,
    },
    }]
  }),
) as unknown as Record<UtilityKey, (typeof UTILITY_DEFINITIONS)[number]['seo']>

const rawSeoRoutes: UmbrellaSeoRoutes<RouteKey> = {
  domain: branding.domain,
  routes: {
    index: {
      path: '/',
      ...ruTranslations.seo.index,
      changefreq: 'weekly',
      priority: 1.0,
    },
    ...utilitySeoRoutes,
    pdfKonverter: {
      path: '/pdf-konverter',
      ...ruTranslations.seo.pdfKonverter,
      changefreq: 'weekly',
      priority: 0.9,
      jsonLd: {
        mode: 'required',
        schemas: buildPdfKonverterSchemas(ruTranslations.umbrella),
      },
    },
    blog: {
      path: '/blog',
      ...ruTranslations.seo.blog,
      changefreq: 'weekly',
      priority: 0.6,
    },
  },
}

export const seoRoutes: UmbrellaSeoRoutes<RouteKey> = {
  ...rawSeoRoutes,
  routes: {
    ...rawSeoRoutes.routes,
    index: {
      ...rawSeoRoutes.routes.index,
      jsonLd: { mode: 'none', reason: 'Umbrella catalog page has no source-owned structured data.' },
    },
    pdfKonverter: {
      ...rawSeoRoutes.routes.pdfKonverter,
    },
    blog: {
      ...rawSeoRoutes.routes.blog,
      jsonLd: { mode: 'none', reason: 'Blog index page; article pages own article schemas.' },
    },
  },
}

export function seoFor(key: RouteKey, locale: Locale = DEFAULT_LOCALE): SeoProps {
  const route = localizedRoute(key, locale)
  return toSeoProps(seoRoutes.domain, route, { locale, defaultImage: branding.ogImage })
}

function localizedRoute(key: RouteKey, locale: Locale) {
  const route = seoRoutes.routes[key]
  if (locale !== DEFAULT_LOCALE && (key === 'index' || key === 'pdfKonverter' || key === 'blog')) {
    const localizedStaticRoute = {
      ...route,
      ...enTranslations.seo[key],
    }
    return key === 'pdfKonverter'
      ? {
        ...localizedStaticRoute,
        jsonLd: {
          mode: 'required' as const,
          schemas: buildPdfKonverterSchemas(enTranslations.umbrella),
        },
      }
      : localizedStaticRoute
  }
  if (locale === DEFAULT_LOCALE || !UTILITY_DEFINITIONS.some((utility) => utility.key === key)) return route

  const utility = buildUtilities(locale === 'en' ? enTranslations : ruTranslations)
    .find((item) => item.key === key)
  if (!utility) throw new Error(`Missing ${locale} source metadata for utility ${key}`)

  const schemas: object[] = [
    buildBreadcrumbSchema([
      { name: branding.domain, url: `https://${branding.domain}/` },
      { name: utility.title, url: `https://${branding.domain}${utility.path}` },
    ]),
    buildSoftwareApplicationSchema({
      name: utility.title,
      description: utility.shortDescription,
      category: 'UtilitiesApplication',
      url: `https://${branding.domain}${utility.path}`,
    }),
  ]
  if (utility.howTo.length > 0) schemas.push(buildHowToSchema(utility.title, utility.howTo))
  if (utility.faq.length > 0) schemas.push(buildFaqSchema(utility.faq))

  return {
    ...route,
    title: utility.title,
    description: utility.shortDescription,
    jsonLd: { mode: 'required' as const, schemas },
  }
}
